import { auth } from "auth"
import { useAsyncData } from "hooks"
import { useEffect } from "react"
import { SCORM_URL } from "../../env"
import { addPlayLinkParams } from "../../tools/addPlayLinkParams"
import { sendVuplexMessage } from "../../vuplex/sendVuplexMessage"

export const useAutoRotation = () => {
  useEffect(() => {
    sendVuplexMessage({
      type: "SET_SCREEN_ORIENTATION",
      payload: { screenOrientation: "AUTO" },
    })

    return () => {
      sendVuplexMessage({
        type: "SET_SCREEN_ORIENTATION",
        payload: { screenOrientation: "PORTRAIT" },
      })
    }
  }, [])
}

export const useWebglLink = (
  productId: string,
  webglLink: string | null,
  useTransferToken: boolean
) => {
  return useAsyncData(
    () =>
      addPlayLinkParams({
        link: webglLink,
        useTransferToken: useTransferToken,
      }),
    [productId]
  )
}

// SCORM builds its own uniform link (company name key in the path) and always
// uses a transfer token, since Babylon Rocker no longer accepts plain JWTs.
// includeCompany is false because the company is already in the path.
export const useScormLink = (productId: string) => {
  return useAsyncData(async () => {
    const { companyNameKey } = await auth.getActiveSession()
    const url = `${SCORM_URL}/company/${companyNameKey}/course/${productId}`
    return auth.addAccessParams(url, {
      useTransferToken: true,
      includeCompany: false,
    })
  }, [productId])
}

// RealTalk is played in an iframe inside the portal on every platform — the
// Skills app never opens it natively (see isHandledByVuplex). addPlayLinkParams
// skips the access params inside Skills+ because the Skills app handles login
// for the products it opens itself; for RealTalk that leaves the iframe with no
// token and the web app bounces to the login page. Both RealTalk targets
// require a transfer token, so always attach one here.
export const addRealTalkLinkParams = async (link: string | null) => {
  if (!link) return

  return auth.addAccessParams(link, {
    useTransferToken: true,
    accessTokenName: "accessToken",
    includeCompany: false,
  })
}

export const useRealTalkWebglLink = (
  productId: string,
  webglLink: string | null
) => {
  return useAsyncData(() => addRealTalkLinkParams(webglLink), [productId])
}

export const useRealTalkStreamLink = (
  productId: string,
  streamLink: string | null
) => {
  const { data, isPending, error } = useAsyncData(
    () => addRealTalkLinkParams(streamLink),
    [productId]
  )

  const isNoLink = !error && !isPending && data === undefined

  return {
    data,
    isPending,
    error: isNoLink ? new Error("No streamLink for product") : error,
  }
}
